import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Drawer, Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import TextArea from "../../components/Inputs/TextAreaInput";
import {
  createPublication,
  deletePublication,
  getPublications,
  updatePublication,
} from "../../services/publicationService";
import DateInput from "../../components/Inputs/DateInput";
import { publicationSchema } from "../../validators/publicationValidators";
import { getInstitutions } from "../../services/institutionService";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { BiShow } from "react-icons/bi";
import { getPublicationTypes } from "../../services/publicationTypesService";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Publication = () => {
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publicationToEdit, setPublicationToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [publicationToDelete, setPublicationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [errs, setErrs] = useState(null);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");
  const [selectedPublicationType, setSelectedPublicationType] =
    useState("Not Selected");

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState(null);

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadPublications();
    loadInstitutions();
    loadPublicationTypes();
  }, []);

  useEffect(() => {
    let filtered = publications;
    if (searchTerm.trim() !== "") {
      filtered = publications.filter((item) =>
        `${item?.title} ${item?.institution?.name} ${item?.authors}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    if (selectedPublicationType !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.publication_type?.id?.toString() ===
          selectedPublicationType?.toString()
      );
    }

    setFilteredPublications(filtered);
  }, [searchTerm, publications, selectedInstitution, selectedPublicationType]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadPublicationTypes = async () => {
    try {
      const { data } = await getPublicationTypes();
      setPublicationTypes(data.data);
    } catch (error) {
      toast.error("Failed to load publication types");
    }
  };

  const loadPublications = async () => {
    setLoading(true);
    try {
      const { data } = await getPublications();

      setPublications(data.data);
      setFilteredPublications(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load publications"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (publicationToEdit) {
      onUpdatePublication(payload, resetForm);
    } else {
      onCreatePublication(payload, resetForm);
    }
  };

  const onCreatePublication = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createPublication(payload);

      setPublications((prev) => [...prev, data.data]);

      toast.success("Publication added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Publication was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setPublicationToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdatePublication = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalPublications = [...publications];

      const publicationIndex = originalPublications.findIndex(
        (publication) => publication.id === publicationToEdit.id
      );

      if (publicationIndex === -1) throw new Error("Publication not found.");

      const { data } = await updatePublication(publicationToEdit.id, payload);

      originalPublications[publicationIndex] = data.data;

      setPublications(originalPublications);

      setPublicationToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Publication updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Publication not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setPublicationToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeletePublication = async () => {
    const originalPublications = [...publications];

    const newPublications = originalPublications.filter(
      (publication) => publication.id !== publicationToDelete.id
    );

    setPublications(newPublications);

    try {
      await deletePublication(publicationToDelete?.id);

      toast.success("Publication deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Publication not deleted, try again later"
        }`
      );

      setPublications(originalPublications);
    } finally {
      setIsDeleteModalOpen(false);

      setPublicationToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setPublicationToEdit(null);
    setPublicationToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Date Published",
      dataIndex: "date_published",
      key: "date_published",
    },
    {
      title: "Authors",
      dataIndex: "authors",
      key: "authors",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "center", color: "#888" }}>
            {displayText}
          </div>
        ) : (
          <>{displayText}</>
        );
      },
    },

    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Publication Type",
      dataIndex: ["publication_type", "name"],
      key: "publication_type",
      render: (text) => (
        <span className="text-gray-700 text-sm">{text || "N/A"}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Publication">
            <button
              className="p-2 border border-blue-200 rounded-md text-blue-600 hover:bg-blue-50 transition"
              onClick={() => {
                setSelectedPublication(record);
                setIsDrawerOpen(true);
              }}
            >
              <BiShow size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Edit Publication">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Publication">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Publications" subtitle1="Publications" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Publications"
            count={filteredPublications.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Publication"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPublications.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              <div className="mb-2 py-2 flex gap-2">
                {!isInstAdmin && (
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                )}

                <Select
                  multiple={false}
                  id="publication_types"
                  defaultValue={selectedPublicationType}
                  className="w-full h-[36px] min-w-[250px]"
                  onChange={(value) => {
                    setSelectedPublicationType(value);
                  }}
                  showSearch={true}
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option value={"Not Selected"}>
                    Filter by Publication Type
                  </Select.Option>
                  {publicationTypes &&
                    publicationTypes.length > 0 &&
                    publicationTypes.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {publicationToEdit ? "Edit Publication" : "Add Publication"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                title: publicationToEdit ? publicationToEdit?.title : "",
                uri: publicationToEdit ? publicationToEdit?.uri : "",
                date_published: publicationToEdit
                  ? publicationToEdit?.date_published
                  : "",
                authors: publicationToEdit ? publicationToEdit?.authors : "",
                institution_id: publicationToEdit
                  ? publicationToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
                publication_type_id: publicationToEdit
                  ? publicationToEdit?.publication_type?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={publicationSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          publicationToEdit
                            ? publicationToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <CustomDropDownInput
                      name="publication_type_id"
                      title="Publication Type"
                      placeholder="Select an Publication Type"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={publicationTypes}
                      setFieldValue={setFieldValue}
                    />

                    <TextInput
                      name="title"
                      title="Publication Title"
                      placeholder="e.g. Advances in Data Science"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <TextInput
                      name="uri"
                      title="URI"
                      placeholder="e.g. https://example.com/publication"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <DateInput
                      name="date_published"
                      title="Date Published"
                      placeholder="Select publication date"
                      errors={errors}
                      values={values}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />

                    <TextArea
                      name="authors"
                      title="Authors"
                      placeholder="e.g. Joel Dickens, Kevin Gates"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeletePublication}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />

          <Drawer
            title={
              <span className="text-lg font-semibold text-gray-700">
                {selectedPublication?.title || "Publication Details"}
              </span>
            }
            width={520}
            onClose={() => setIsDrawerOpen(false)}
            open={isDrawerOpen}
            placement="right"
          >
            {selectedPublication ? (
              <div className="text-sm text-gray-700 space-y-4">
                <p>
                  <strong>Institution:</strong>{" "}
                  {selectedPublication.institution?.name || "N/A"}
                </p>
                <p>
                  <strong>Title:</strong> {selectedPublication.title}
                </p>
                <p>
                  <strong>URI:</strong>{" "}
                  {selectedPublication.uri ? (
                    <a
                      href={selectedPublication.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline"
                    >
                      {selectedPublication.uri}
                    </a>
                  ) : (
                    "—"
                  )}
                </p>
                <p>
                  <strong>Date Published:</strong>{" "}
                  {selectedPublication.date_published || "N/A"}
                </p>
                <div>
                  <strong>Authors:</strong>
                  <p className="mt-1 text-gray-600">
                    {selectedPublication.authors || "N/A"}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No publication selected.</p>
            )}
          </Drawer>
        </div>
      </div>
    </>
  );
};

export default Publication;
